/**
 * The receive mixer (web-plan.md F4): three voice slots and the share-audio
 * lane, each through its own GainNode so a per-person volume (0..2, D33's
 * range) lands on whatever slot that person currently occupies.
 *
 * Chrome quirk that shapes the plumbing: a remote MediaStreamTrack feeds a
 * MediaStreamAudioSourceNode only while it is ALSO sinked somewhere, so each
 * slot keeps a muted `<audio>` element whose only job is to make the track
 * flow. The audible path is WebAudio's.
 */
export const VOICE_SLOTS = 3;
/** The share-audio lane's slot index, after the voice slots (F5 plays it). */
export const SHARE_AUDIO_SLOT = VOICE_SLOTS;

interface Lane {
  el: HTMLAudioElement;
  gain: GainNode;
}

export class Mixer {
  private ctx: AudioContext;
  private master: GainNode;
  private lanes: Record<number, Lane> = {};

  constructor() {
    this.ctx = new AudioContext({ sampleRate: 48_000 });
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);
  }

  /** Autoplay policy: called from the join click, the one blessed gesture. */
  async resume(): Promise<void> {
    if (this.ctx.state !== "running") await this.ctx.resume().catch(() => {});
  }

  attach(slot: number, track: MediaStreamTrack): void {
    const stream = new MediaStream([track]);
    const el = new Audio();
    el.srcObject = stream;
    el.muted = true;
    void el.play().catch(() => {});
    const gain = this.ctx.createGain();
    this.ctx.createMediaStreamSource(stream).connect(gain);
    gain.connect(this.master);
    this.lanes[slot] = { el, gain };
  }

  setSlotGain(slot: number, value: number): void {
    const lane = this.lanes[slot];
    if (lane) lane.gain.gain.value = value;
  }

  /** Deafen is the master, not per-lane: one decision, one node. */
  setDeafened(deafened: boolean): void {
    this.master.gain.value = deafened ? 0 : 1;
  }

  close(): void {
    for (const lane of Object.values(this.lanes)) {
      lane.el.pause();
      lane.el.srcObject = null;
    }
    this.lanes = {};
    void this.ctx.close().catch(() => {});
  }
}
